import React, { useState, useMemo, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Typography,
  Popover,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  TableSortLabel,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  Button,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import UserTeamInfo from './UserTeamInfo';

const TableControls = ({ 
  searchTerm, 
  onSearchChange, 
  selectedStatus,
  onStatusChange,
  totalResults,
  funderFilter,
  onFunderFilterChange,
  availableFunders 
}) => {
  const statusFilters = [
    { value: 'all', label: 'All Users' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'pending', label: 'Pending Setup' },
    { value: 'needs_invite', label: 'Needs Invite' },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 2,
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
      }}>
        <TextField
          placeholder="Search users by name, email, role..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ 
            minWidth: { xs: '100%', md: '300px' },
            backgroundColor: 'white',
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          size="small"
        />

        <FormControl 
          size="small" 
          sx={{ 
            minWidth: { xs: '100%', md: '220px' },
            backgroundColor: 'white',
          }}
        >
          <Select
            value={funderFilter || ''}
            onChange={(e) => onFunderFilterChange(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">All Funders</MenuItem>
            <MenuItem value="none">No Funder</MenuItem>
            {availableFunders.map(funder => (
              <MenuItem key={funder} value={funder}>
                {funder}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={selectedStatus}
          exclusive
          onChange={(e, newValue) => {
            if (newValue !== null) {
              onStatusChange(newValue);
            }
          }}
          size="small"
          sx={{ 
            flexWrap: 'wrap',
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 2,
            },
          }}
        >
          {statusFilters.map((filter) => (
            <ToggleButton 
              key={filter.value} 
              value={filter.value}
            >
              {filter.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Typography variant="body2" color="text.secondary">
        {totalResults} {totalResults === 1 ? 'user' : 'users'} found
      </Typography>
    </Box>
  );
};

const UserDetailsPopover = ({ user, open, anchorEl, onClose, getRoleLabel = (role) => role }) => {
  if (!user) return null;

  const sections = [
    {
      title: 'Account Details',
      fields: [
        { label: 'Email', value: user.email },
        { label: 'Full Name', value: user.name },
        { label: 'Department', value: user.department || '-' },
        { label: 'Role', value: getRoleLabel(user.role) },
        { label: 'Funder', value: user.funder || '-' } // Add this line
      ]
    },
    {
      title: 'Account Status',
      fields: [
        { 
          label: 'Status', 
          value: user.disabled ? 'Suspended' : 'Active',
          chip: true,
          color: user.disabled ? 'error' : 'success'
        },
        { 
          label: 'Last Invite', 
          value: user.lastInviteSent ? new Date(user.lastInviteSent).toLocaleString() : 'Never'
        },
        {
          label: 'Last Sign In',
          value: user.lastSignInTime ? new Date(user.lastSignInTime).toLocaleString() : 'Never'
        }
      ]
    },
    // New section for password history
    {
      title: 'Password History',
      fields: [
        {
          label: 'Password Created',
          value: user.passwordCreatedAt ? new Date(user.passwordCreatedAt).toLocaleString() : 'Not yet created'
        },
        {
          label: 'Last Password Reset',
          value: user.lastPasswordReset ? new Date(user.lastPasswordReset).toLocaleString() : 'Never'
        },
        {
          label: 'Setup Attempts',
          value: user.passwordSetupAttempts || 0
        },
        {
          label: 'Failed Sign-ins',
          value: user.failedSignInAttempts || 0,
          chip: user.failedSignInAttempts > 3,
          color: 'warning'
        }
      ]
    }
  ];

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{ mt: 1 }}
    >
      <Card sx={{ width: 400 }}>
        <CardContent>
          {sections.map((section, idx) => (
            <Box key={section.title} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {section.title}
              </Typography>
              <Box sx={{ '& > div': { mb: 1 } }}>
                {section.fields.map(field => (
                  <Box 
                    key={field.label} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center' 
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {field.label}:
                    </Typography>
                    {field.chip ? (
                      <Chip
                        label={field.value}
                        color={field.color}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {field.value}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
              {idx < sections.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </CardContent>
      </Card>
    </Popover>
  );
};

const UserStatusChip = ({ user }) => {
  let color = 'default';
  let label = 'Unknown';
  let icon = null;
  let tooltipText = ''; 

  if (user.disabled) {
    color = 'error';
    label = 'Suspended';
    icon = <LockIcon sx={{ fontSize: '1rem' }} />;
    tooltipText = 'User account has been suspended and cannot access the system';
  }
  // User has logged in before
  else if (user.lastSignInTime) {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const lastSignIn = new Date(user.lastSignInTime);
    
    if (lastSignIn > thirtyDaysAgo) {
      color = 'success';
      label = 'Active';
      icon = <LockOpenIcon sx={{ fontSize: '1rem' }} />;
      tooltipText = 'User has logged in within the last 30 days';
    } else {
      color = 'secondary'; // Purple for inactive
      label = 'Inactive';
      icon = <LockOpenIcon sx={{ fontSize: '1rem' }} />;
      tooltipText = 'User has not logged in for more than 30 days';
    }
  }
  // User has created password but hasn't logged in
  else if (user.passwordCreatedAt) {
    color = 'info'; 
    label = 'Password Created';
    tooltipText = 'User has set up their password but has not logged in yet';
  }
  // User has been invited but hasn't set up password
  else if (user.lastInviteSent) {
    color = 'warning'; // Orange for pending setup
    label = 'Setup Pending';
    tooltipText = 'User has been invited but has not completed password setup';
  }
  // User hasn't been invited yet
  else {
    color = 'default'; // Grey for needs invite
    label = 'Needs Invite';
    tooltipText = 'User account created but invitation has not been sent yet';
  }

  return (
    <Tooltip title={tooltipText} arrow placement="right">
      <Chip
        icon={icon}
        label={label}
        color={color}
        variant="outlined"
        size="small"
        sx={{ 
          minWidth: 110,
          '& .MuiChip-label': {
            fontWeight: 500
          }
        }}
      />
    </Tooltip>
  );
};

  const UserTable = ({ 
    users, 
    usersEnriched = true,
    onSendInvite, 
    onEdit, 
    onSuspend, 
    onDelete,
    loading = false,
    onSort = () => {},
    sortConfig = { key: null, direction: 'asc' },
    invitingUser = null,
    availableTeams = [],
    onBatchInvite, 
    batchSending = false,
    onBulkDelete, 
    bulkDeleteInProgress = false,
    onExportActivationLinks,
    exportingLinks = false,
    deletingUser,
    page = 0,  
    totalCount = 0,  
    pageSize = 10,  
    onPageChange = () => {},  
    onPageSizeChange = () => {},
    // External filter props
    externalSearchTerm = null,
    externalSelectedStatus = null,
    externalFunderFilter = null,
    hideControls = false,
    getRoleLabel = (role) => role
  }) => {
  
  const [detailsAnchor, setDetailsAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviteErrors, setInviteErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [funderFilter, setFunderFilter] = useState('');

  // Use external filter values when provided
  const activeSearchTerm = externalSearchTerm !== null ? externalSearchTerm : searchTerm;
  const activeSelectedStatus = externalSelectedStatus !== null ? externalSelectedStatus : selectedStatus;
  const activeFunderFilter = externalFunderFilter !== null ? externalFunderFilter : funderFilter;

  // Calculate available funders from user data
  const availableFunders = useMemo(() => {
    const funders = new Set();
    users.forEach(user => {
      if (user.funder) {
        funders.add(user.funder);
      }
    });
    return Array.from(funders).sort();
  }, [users]);

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = activeSearchTerm.toLowerCase().trim() === '' || 
        Object.entries(user).some(([key, value]) => {
          // Only search through specific fields
          if (!['name', 'email', 'role', 'network', 'region', 'funder'].includes(key)) {
            return false;
          }
          return String(value).toLowerCase().includes(activeSearchTerm.toLowerCase().trim());
        });

      let matchesStatus = true;
      switch (activeSelectedStatus) {
        case 'active':
          matchesStatus = !user.disabled && user.lastSignInTime && 
            new Date(user.lastSignInTime) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'inactive':
          matchesStatus = !user.disabled && user.lastSignInTime && 
            new Date(user.lastSignInTime) <= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'suspended':
          matchesStatus = user.disabled;
          break;
        case 'pending':
          matchesStatus = !user.lastSignInTime;
          break;
        case 'needs_invite':
          matchesStatus = !user.disabled && !user.lastSignInTime && !user.passwordCreatedAt && !user.lastInviteSent;
          break;
        default:
          matchesStatus = true;
      }

      let matchesFunder = true;
      if (activeFunderFilter === 'none') {
        matchesFunder = !user.funder;
      } else if (activeFunderFilter) {
        matchesFunder = user.funder === activeFunderFilter;
      }

      return matchesSearch && matchesStatus && matchesFunder;
    });
  }, [users, activeSearchTerm, activeSelectedStatus, activeFunderFilter]);

  const handleOpenDetails = (event, user) => {
    setDetailsAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleCloseDetails = () => {
    setDetailsAnchor(null);
    setSelectedUser(null);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.uid));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectUser = (uid) => {
    setSelectedUsers(prev => 
      prev.includes(uid) 
        ? prev.filter(id => id !== uid)
        : [...prev, uid]
    );
  };

  const handleInvite = async (user) => {
    try {
      const userToInvite = {
        ...user,
        firstName: user.firstName || (user.name ? user.name.split(' ')[0] : ''),
        lastName: user.lastName || (user.name ? user.name.split(' ').slice(1).join(' ') : ''),
        // Ensure funder is also passed along if present
        funder: user.funder || null
      };
      await onSendInvite(userToInvite);
    } catch (error) {
      console.error('Send invite error:', error);
      if (error.status === 429) {
        // Parse the response to get the rate limit details
        const errorData = await error.json();
        const nextAvailable = new Date(errorData.nextAvailableAt);
        
        setInviteErrors({
          ...inviteErrors,
          [user.uid]: {
            message: errorData.message || 'Please wait before sending another invite',
            nextAvailable
          }
        });
  
        // Clear error after rate limit expires
        const timeUntilAvailable = nextAvailable.getTime() - Date.now();
        setTimeout(() => {
          setInviteErrors(prev => {
            const updated = { ...prev };
            delete updated[user.uid];
            return updated;
          });
        }, timeUntilAvailable);
      } else {
        setInviteErrors({
          ...inviteErrors,
          [user.uid]: {
            message: 'Failed to send invite. Please try again later.',
            nextAvailable: new Date(Date.now() + 5000) // Show for 5 seconds
          }
        });
  
        // Clear general error after 5 seconds
        setTimeout(() => {
          setInviteErrors(prev => {
            const updated = { ...prev };
            delete updated[user.uid];
            return updated;
          });
        }, 5000);
      }
    }
  };

  useEffect(() => {
    setSelectedUsers([]);
    setSelectAll(false);
  }, [activeSearchTerm, activeSelectedStatus, activeFunderFilter]);

  const exportToCSV = () => {
    const headers = ['User ID', 'Name', 'Email', 'Role', 'Funder', 'Teams', 'Team Groups', 'Status', 'Last Login'];
    const rows = filteredUsers.map(u => {
      let status = 'Needs Invite';
      if (u.disabled) status = 'Suspended';
      else if (u.lastSignInTime) {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        status = new Date(u.lastSignInTime) > thirtyDaysAgo ? 'Active' : 'Inactive';
      } else if (u.passwordCreatedAt) status = 'Password Created';
      else if (u.lastInviteSent) status = 'Setup Pending';

      return [
        u.uid || '',
        (u.firstName && u.lastName) ? `${u.firstName} ${u.lastName}` : (u.name || ''),
        u.email || '',
        getRoleLabel(u.role) || '',
        u.funder || '',
        (u.teams || []).map(t => t.name || t.id || t).join('; '),
        (u.teamGroups || []).map(g => g.name || g.id || g).join('; '),
        status,
        u.lastSignInTime ? new Date(u.lastSignInTime).toLocaleString() : 'Never'
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [headers.map(h => `"${h}"`).join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const renderInviteButton = (user) => {
    const error = inviteErrors[user.uid];
    const isRateLimited = Boolean(error);
    
    return (
      <Tooltip 
        title={
          isRateLimited 
            ? `${error.message}${error.nextAvailable ? ` (Available at ${error.nextAvailable.toLocaleTimeString()})` : ''}`
            : "Send/Resend Invite"
        }
      >
        <span>
          <IconButton
            onClick={() => handleInvite(user)}
            color="primary"
            size="small"
            disabled={invitingUser === user.uid || isRateLimited}
          >
            {invitingUser === user.uid ? (
              <CircularProgress size={20} />
            ) : (
              <EmailIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const sortedColumns = {
    status: 'User Status',
    name: 'Name',
    funder: 'Funder', 
    lastActivity: 'Last Login'
  };

  return (
    <Box>
      {!hideControls && (
        <TableControls
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          funderFilter={funderFilter}
          onFunderFilterChange={setFunderFilter}
          availableFunders={availableFunders}
          totalResults={filteredUsers.length}
        />
      )}
      
      {/* Results count and Export CSV when controls are hidden */}
      {hideControls && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'} found
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={exportToCSV}
            disabled={filteredUsers.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      )}
      
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <TableContainer 
          component={Paper} 
          sx={{ maxHeight: 600, overflow: 'auto' }}
          elevation={0}
        >
          <Table stickyHeader>
          <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50', width: 50 }}>
              <Checkbox
                checked={selectAll}
                onChange={handleSelectAll}
                indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
              />
            </TableCell>
                {Object.entries(sortedColumns).map(([key, label]) => (
                  <TableCell 
                    key={key}
                    sx={{ fontWeight: 600, backgroundColor: 'grey.50' }}
                    sortDirection={sortConfig.key === key ? sortConfig.direction : false}
                  >
                    <TableSortLabel
                      active={sortConfig.key === key}
                      direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
                      onClick={() => onSort(key)}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50' }}>
                  Details
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50' }}>
                  Actions
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50' }}>
                  Assigned Teams
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow 
                  key={user.uid}
                  sx={{ 
                    '&:hover': { backgroundColor: 'grey.50' },
                    backgroundColor: user.disabled ? 'grey.50' : 'inherit'
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.includes(user.uid)}
                      onChange={() => handleSelectUser(user.uid)}
                    />
                  </TableCell>
                  <TableCell>
                    {!usersEnriched ? (
                      <Typography variant="body2" color="text.secondary">Loading…</Typography>
                    ) : (
                      <UserStatusChip user={user} />
                    )}
                  </TableCell>
                  <TableCell>
                  <Typography sx={{ fontWeight: 500 }}>
                    {(user.firstName && user.lastName) 
                      ? `${user.firstName} ${user.lastName}` 
                      : user.name || user.email}
                  </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getRoleLabel(user.role)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {!usersEnriched ? (
                      <Typography variant="body2" color="text.secondary">Loading…</Typography>
                    ) : user.funder ? (
                      <Typography variant="body2">
                        {user.funder}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No funder assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      {!usersEnriched ? (
                        <Typography variant="body2" color="text.secondary">Loading…</Typography>
                      ) : user.lastSignInTime ? (
                        <Typography variant="body2">
                          {new Date(user.lastSignInTime).toLocaleString()}
                        </Typography>
                      ) : user.passwordCreatedAt ? (
                        <Typography variant="body2" color="info.main">
                          Password Created: {new Date(user.passwordCreatedAt).toLocaleDateString()}
                        </Typography>
                      ) : user.lastInviteSent ? (
                        <Typography variant="body2" color="warning.main">
                          Invited: {new Date(user.lastInviteSent).toLocaleDateString()}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Never logged in
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => handleOpenDetails(e, user)}
                      color="primary"
                      size="small"
                    >
                      <InfoIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {renderInviteButton(user)}
                      <Tooltip title="Edit User">
                        <IconButton
                          onClick={() => onEdit(user)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.disabled ? "Reactivate User" : "Suspend User"}>
                        <IconButton
                          onClick={() => onSuspend(user)}
                          color={user.disabled ? "success" : "warning"}
                          size="small"
                        >
                          {user.disabled ? <LockOpenIcon /> : <LockIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete User">
                        <span> {/* Wrap in span to maintain tooltip when disabled */}
                          <IconButton
                            onClick={() => onDelete(user)}
                            color="error"
                            size="small"
                            disabled={deletingUser === user.uid}
                          >
                            {deletingUser === user.uid ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {!usersEnriched ? (
                      <Typography variant="body2" color="text.secondary">Loading…</Typography>
                    ) : (
                      <UserTeamInfo 
                        user={{
                          ...user,
                          teams: user.teams || [],
                          teamGroups: user.teamGroups || []
                        }} 
                        availableTeams={availableTeams}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
  
        <UserDetailsPopover
          user={selectedUser}
          open={Boolean(detailsAnchor)}
          anchorEl={detailsAnchor}
          onClose={handleCloseDetails}
          getRoleLabel={getRoleLabel}
        />
      </Box>
      {/* Batch Actions UI */}
      {selectedUsers.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderRadius: 2,
            boxShadow: 3,
            bgcolor: 'background.paper'
          }}
        >
          <Typography sx={{ fontWeight: 500 }}>
            {selectedUsers.length} {selectedUsers.length === 1 ? 'user' : 'users'} selected
          </Typography>
          
          {/* Existing batch invite button */}
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            onClick={() => onBatchInvite(selectedUsers)}
            disabled={batchSending || bulkDeleteInProgress}
          >
            {batchSending ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Sending Invites...
              </>
            ) : (
              'Send Invites'
            )}
          </Button>
          
          {/* Export activation links button */}
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={() => onExportActivationLinks(selectedUsers)}
            disabled={batchSending || bulkDeleteInProgress || exportingLinks}
          >
            {exportingLinks ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Generating...
              </>
            ) : (
              'Export Activation Links'
            )}
          </Button>

          {/* New bulk delete button */}
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => onBulkDelete(selectedUsers)}
            disabled={batchSending || bulkDeleteInProgress || exportingLinks}
          >
            {bulkDeleteInProgress ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Deleting...
              </>
            ) : (
              'Delete Selected'
            )}
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => setSelectedUsers([])}
            disabled={batchSending || bulkDeleteInProgress || exportingLinks}
          >
            Clear Selection
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default UserTable;